export enum SettingCategory {
    GENERAL = 'general',
    THEATER = 'theater',
    BOOKING = 'booking',
    DISPLAY = 'display',
    EMAIL = 'email',
    PAYMENT = 'payment',
    SERVER = 'server',
}

export enum SettingType {
    STRING = 'string',
    NUMBER = 'number',
    BOOLEAN = 'boolean',
    IMAGE = 'image',
    JSON = 'json',
}

export enum GoogleMapsType {
    IFRAME = 'iframe',
    GOOGLE_MAPS = 'googleMaps',
}

export enum SettingKey {
    DEFAULT_MAX_CAPACITY = 'defaultMaxCapacity',
    DEFAULT_MAX_CHILDREN_CAPACITY = 'defaultMaxChildrenCapacity',
    LIMITED_AVAILABILITY_THRESHOLD = 'limitedAvailabilityThreshold',
    AVAILABLE_SHOW_COLOR = 'availableShowColor',
    ALMOST_FULL_SHOW_COLOR = 'almostFullShowColor',
    FULL_SHOW_COLOR = 'fullShowColor',
    SEND_EMAIL_ON_BOOKING = 'sendEmailOnBooking',
    ENABLE_PAYMENT_CUSTOMER_DETAILS = 'enablePaymentCustomerDetails',
    ENABLE_API_PAYMENT_CONNECTION = 'enableApiPaymentConnection',
    CHILD_TICKET_PRICE = 'childTicketPrice',
    STANDARD_TICKET_PRICE = 'standardTicketPrice',
    VIP_TICKET_PRICE = 'vipTicketPrice',
    VIP_TICKET_CHILD_PRICE = 'vipTicketChildPrice',
    PAGE_CONTENT = 'pageContent',
    VIVA_PAYMENT_CONFIGURATION = 'vivaPaymentConfiguration',
    CARDLINK_PAYMENT_CONFIGURATION = 'cardlinkPaymentConfiguration',
    GMAIL_SMTP_CONFIGURATION = 'gmailSmtpConfiguration',
    VIP_IMAGE_URL = 'vipImageUrl',
    GOOGLE_MAPS_TYPE = 'googleMapsType',
    ADDITIONAL_FEE_PERCENTAGE = 'additionalFeePercentage',
    MAINTENANCE_MODE = 'maintenanceMode',
    SHOW_GROUP_DEALS_BANNER = 'showGroupDealsBanner',
    ATLAS_BACKUP_URI = 'atlasBackupUri',
    ENABLE_ATLAS_BACKUP = 'enableAtlasBackup',
    ENABLE_CLEAN_OLD_JOBS = 'enableCleanOldJobs',
    ENABLE_CLEAN_OLD_LOGS = 'enableCleanOldLogs',
    ENABLE_ERROR_ALERTS = 'enableErrorAlerts',
    ENABLE_ERROR_ALERT_CAPTURE = 'enableErrorAlertCapture',
    ENABLE_SYNC_SHOW_CAPACITY = 'enableSyncShowCapacity',
    SYNC_SHOW_CAPACITY_DAYS = 'syncShowCapacityDays',
    CALENDAR_ACTIVATED_SEASONS = 'calendarActivatedSeasons',
    ACTIVE_PAYMENT_SERVICE = 'activePaymentService',
    ENABLE_SEASON_CALENDAR = 'enableSeasonCalendar',
    TRAVEL_INSURANCE_PRICE = 'travelInsurancePrice',
    BANNER_MODAL_CONFIG = 'bannerModalConfig',
    VOLCANO_BANNER_MODAL_CONFIG = 'volcanoBannerModalConfig',
    TRANSPORTATION_CONFIG = 'transportationConfig',
}

export enum BannerModalPosition {
    TOP_LEFT = 'top-left',
    TOP_CENTER = 'top-center',
    TOP_RIGHT = 'top-right',
    CENTER = 'center',
    BOTTOM_LEFT = 'bottom-left',
    BOTTOM_CENTER = 'bottom-center',
    BOTTOM_RIGHT = 'bottom-right',
}

export interface BannerModalConfig {
    enabled: boolean;
    title: string;
    text: string;
    backgroundColor: string;
    textColor: string;
    borderColor: string;
    fontSize: number;
    position: BannerModalPosition;
}

export enum VolcanoBannerImageTextPosition {
    TOP = 'top',
    BOTTOM = 'bottom',
}

export interface VolcanoBannerModalConfig {
    enabled: boolean;
    title: string;
    text: string;
    imageUrl: string;
    showImageText: boolean;
    imageText: string;
    imageTextPosition: VolcanoBannerImageTextPosition;
    backgroundColor: string;
    textColor: string;
    borderColor: string;
    fontSize: number;
    position: BannerModalPosition;
}

export interface Setting {
    _id?: string;
    setting_key: string | SettingKey;
    setting_value: string | number | boolean | object;
    description?: string;
    category?: string | SettingCategory;
    setting_type?: string | SettingType;
    setting_created_at?: Date;
    setting_updated_at?: Date;
    deleted?: number;
}

export interface VivaPaymentConfig {
    source_code: string;
    api_url: string;
    auth_url: string;
    payment_url: string;
    client_id: string;
    client_secret: string;
    default_payment_method_code: string;
    checkout_color: string;
    viva_connect_url: string;
    viva_create_orders_url: string;
    viva_web_checkout_url: string;
    viva_api_orders_url: string;
    merchant_id: string;
    api_key: string;
}

export interface VivaPaymentConfigMain {
    demo: VivaPaymentConfig;
    production: VivaPaymentConfig;
    activeConfig: 'demo' | 'production';
}

export interface CardlinkPaymentConfig {
    merchant_id: string;
    shared_secret: string;
    post_url: string;
    currency: string;
    confirm_url: string;
    cancel_url: string;
    business_partner: 'cardlink' | 'nexi' | 'worldline';
    language: string;
    payment_method: string;
    installments: string;
    css_url?: string;
}

export interface CardlinkPaymentConfigMain {
    demo: CardlinkPaymentConfig;
    production: CardlinkPaymentConfig;
    activeConfig: 'demo' | 'production';
}

export interface GmailSmtpConfig {
    service: string;
    secure: boolean;
    smtp_from: string;
    theater_email: string;
    auth: {
        user: string;
        pass: string;
    }
}

export interface CalendarActivatedSeason {
    year: number;
    monthsEnabled: number[];
    status: 'enabled' | 'disabled';
}

export interface CalendarActivatedSeasonsResponse {
    seasons: CalendarActivatedSeason[];
}

export enum ActivePaymentService {
    VIVA = 'viva',
    CARDLINK = 'cardlink'
}

export interface TransportationDestination {
    name: string;
    price: number;
}

export interface TransportationConfig {
    enabled: boolean;
    globalPrice: number;
    destinations: TransportationDestination[];
}